import React, { useEffect, useMemo, useRef } from "react";
import times from "lodash/times";
import submit from "submit-form";
import { useTranslation } from "react-i18next";

import WorkoutPreview from "./components/workout-preview";
import ProgramInsightsShell from "../../athlete-portal/programs/program-insights/components/ProgramInsightsShell";
import { buildProgramPreviewInsightsInput } from "../../athlete-portal/programs/program-insights/utils/inputs/buildProgramPreviewInsightsInput";
import { getEffectiveDefaultInsightsLens } from "../../../constants/default-insights-lens";

import SvgIcon from "../../../parts/SvgIcon";
import { truncateText } from "../../../utils/string-helpers";
import { isPushOrPerformClient } from "../../../utils/user-helpers";
import { getRepMaxForExercise } from "../../../utils/complex";
import { useRillContext } from "../../../rill/RillReact/share/base";

const formatExercises = (exercises, user, complexes = []) => {
  const newWorkouts = {};
  Object.keys(exercises).map((weekSession) => {
    Object.keys(exercises[weekSession]).map((key) => {
      const newExercises = exercises[weekSession][key].map((exercise) => {
        exercise.repMaximums = user.repMaximums;
        exercise.sets = exercise.sets.map((set) => {
          if (set.hasOwnProperty("percentRepMax")) {
            // Use complex-aware rep max calculation
            const repMax = getRepMaxForExercise(exercise, user, complexes);

            // Save secondary RM on the exercise if it's a secondary exercise
            if (exercise.type === "secondary") {
              exercise.secondaryRepMaximum = repMax;
            }

            // Calculate the load of the set
            set.load = (set.percentRepMax / 100) * repMax;
          }
          return set;
        });

        return exercise;
      });
      newWorkouts[weekSession] = {
        ...newWorkouts[weekSession],
        [key]: newExercises,
      };
    });
  });

  return newWorkouts;
};

const handleProgramChange = (program) => {
  let programPath = `/actions/refresh?week=1`;
  submit(programPath, {
    body: { program: program + 1 },
  });
};

function View(props) {
  const context = useRillContext();
  const { t } = useTranslation("dashboard");

  const { locals } = context;

  const {
    editCycle: cycle,
    cycleExercises,
    editExercises,
    activeProgramIndex,
    activeUser,
    query,
    heading,
    activeUserToRequest,
    complexes,
    fromMetric,
    organizationOwner,
    clientConfiguration,
  } = locals;

  const workoutAreaRef = useRef(null);

  const program = cycle.programs[activeProgramIndex];
  const sessions = program && program.sessions ? program.sessions : [];

  const isCoach = cycle.type === "coach";
  const isSubscription = cycle.type === "subscription";
  const isAthlete =
    (activeUser.coach && !activeUser.coach.inactive) ||
    isPushOrPerformClient(activeUserToRequest);
  const isPushCoach = Boolean(activeUserToRequest.pushAccountInfo);
  const inCoachMode =
    isCoach &&
    String(cycle.createdBy._id) === String(activeUserToRequest._id) &&
    heading !== "Program Builder";
  const inPushCoachMode =
    isSubscription &&
    String(cycle.createdBy._id) === String(activeUserToRequest._id) &&
    heading !== "Program Builder";
  // If the user is a coach that is in the Athlete Portal:
  // Return the Athlete's load. Otherwise, return the Coach's load.
  const newExercises = formatExercises(
    cycleExercises.exercises,
    query.athlete ? locals.athlete : activeUserToRequest,
    complexes || []
  );

  let organizationQuery = ``;

  if (query.organizationCoach) {
    organizationQuery = `&organizationCoach=${query.organizationCoach}`;
  }

  let backPath = inCoachMode
    ? "/coaching-portal/all-templates?owner=coach"
    : "/dashboard/program-library";

  if (query.athlete) {
    backPath =
      `/athlete-portal/programs/?athlete=${query.athlete}` + organizationQuery;
  } else if (isPushCoach && inPushCoachMode) {
    const isPublished = cycle.status === "published";
    const storeType = cycle.storeType || "programs";

    backPath = `/coaching-portal/store?status=${
      isPublished ? "published" : "draft"
    }&storeType=${storeType}`;
  }

  if (isAthlete) {
    backPath = "/dashboard/program-builder";
  }

  const count = useMemo(
    () =>
      program.sessions.reduce((prev, session) => {
        if (!prev.includes(session.week)) return [...prev, session.week];
        return prev;
      }, []).length,
    [program]
  );

  useEffect(() => {
    setExerciseListHeight();
  }, []);

  const setExerciseListHeight = () => {
    for (let n = 0; n < 7; n++) {
      const className = `workout-${n + 1}-list`;
      // gets one row of workouts
      const elements = Array.from(document.getElementsByClassName(className));

      elements.forEach((el) => {
        el.style.minHeight = "0";
      });
      const elementsHeights = elements.map((el) => el.clientHeight);
      const maxHeight = Math.max(...elementsHeights);
      // set the min-height of all elements in the row to the same height as the
      // tallest element in it, without clipping content taller than that measurement
      elements.forEach((el) => {
        el.style.minHeight = `${maxHeight}px`;
      });
    }
  };

  const notSingleWorkout = cycle.storeType !== "single-workout";
  const showProgramInsightsShell = cycle.type !== "user" && notSingleWorkout;

  // Build the program insights input for the program preview
  const programInsightsInput = useMemo(() => {
    if (!showProgramInsightsShell) {
      return null;
    }
    const exercisesSource =
      Array.isArray(editExercises) && editExercises.length > 0
        ? editExercises
        : cycleExercises;
    return buildProgramPreviewInsightsInput(program, exercisesSource);
  }, [showProgramInsightsShell, program, editExercises, cycleExercises]);

  return (
    <main
      className={`program-preview${
        showProgramInsightsShell && programInsightsInput
          ? " program-preview--insights-shell"
          : ""
      }`}
    >
      <header>
        <hgroup>
          <a href={backPath}>
            <button data-button-type="tertiary" data-icon-only>
              <SvgIcon src={"/img/new-icons/arrow-square-left.svg"} />
            </button>
            <span>{t("programOverview.preview")}</span>
          </a>
        </hgroup>

        {notSingleWorkout && (
          <hgroup>
            <div role="month-handler">
              <div>
                <button
                  onClick={() => handleProgramChange(activeProgramIndex - 1)}
                  disabled={activeProgramIndex === 0}
                  data-button-type="text-white"
                  data-icon-only
                >
                  <SvgIcon src="/img/new-icons/arrow-left.svg" alt="prev" />
                </button>
                <span className="flex-shrink-0">
                  {truncateText(cycle?.programs[activeProgramIndex]?.name, 50)}
                </span>
                <button
                  onClick={() => handleProgramChange(activeProgramIndex + 1)}
                  disabled={activeProgramIndex === cycle?.programs.length - 1}
                  data-button-type="text-white"
                  data-icon-only
                >
                  <SvgIcon src="/img/new-icons/arrow-right.svg" alt="next" />
                </button>
              </div>
            </div>
          </hgroup>
        )}

        <hgroup></hgroup>
      </header>
      <div
        className={`ProgramEditor-insights-body${
          showProgramInsightsShell && programInsightsInput
            ? " ProgramEditor-insights-body--stacked"
            : ""
        }`}
      >
        <section ref={workoutAreaRef} className="Main Workout-Area">
          <div className="weeks">
            {times(count).map((weekNumber) => {
              return (
                <div
                  className="week"
                  key={`${cycle._id}-program${activeProgramIndex}-week${
                    weekNumber + 1
                  }`}
                >
                  {notSingleWorkout && (
                    <header>
                      <p className="s2 clipped">
                        {t("programPreview.weekNumber", {
                          number: weekNumber + 1,
                        })}
                      </p>
                    </header>
                  )}
                  {newExercises[weekNumber + 1] &&
                    Object.keys(newExercises[weekNumber + 1]).map(
                      (workoutNumber) => {
                        return (
                          <WorkoutPreview
                            locals={locals}
                            workoutNumber={workoutNumber}
                            week={weekNumber + 1}
                            exercises={
                              newExercises[weekNumber + 1][workoutNumber]
                            }
                            sessions={sessions}
                            key={`${cycle._id}-program${activeProgramIndex}-week${
                              weekNumber + 1
                            }-workout${workoutNumber}`}
                            notSingleWorkout={notSingleWorkout}
                          />
                        );
                      }
                    )}
                </div>
              );
            })}
          </div>
        </section>

        {showProgramInsightsShell && programInsightsInput && (
          <div className="Program-Editor Program-Editor--insights-host-only">
            <ProgramInsightsShell
              key={`${cycle._id}-${activeProgramIndex}`}
              cycleId={cycle._id}
              builderScrollRef={workoutAreaRef}
              builderWeeks={programInsightsInput.builderWeeks}
              programExercises={programInsightsInput.programExercises}
              fromMetric={fromMetric}
              coachPreferences={{
                ...activeUserToRequest,
                defaultInsightsLens: getEffectiveDefaultInsightsLens(
                  activeUser,
                  organizationOwner,
                  clientConfiguration,
                ),
              }}
            />
          </div>
        )}
      </div>
    </main>
  );
}

export default View;
